/**
 * Footer, pure half.
 *
 * Moved verbatim from components/FlowBuilder/flow-components/components/FooterComponent.js. Runs unchanged in the
 * browser and, via the flow-core bundle, inside GraalJS on the backend, so it
 * must never reach for React, the DOM, Node APIs, or the network.
 */

/**
 * A payload is a map of names to values, never anything else.
 *
 * Meta refuses any other shape, `Expected property 'payload' to be of type 'object' but found
 * 'string'`, so a flow that arrives carrying one (imported, hand-uploaded, or saved before
 * 2026-09-23, when the booking template still completed with the bare reference string
 * `${data.extension_message_response}`) is repaired here on the way out rather than kept and
 * refused. Dropping it costs nothing: it was never deliverable.
 */
const objectPayload = (payload) =>
  (payload && typeof payload === 'object' && !Array.isArray(payload) ? payload : {});

// Each Footer action type carries only its own fields. Strip the rest so we never emit invalid Flow
// JSON, e.g. a `next` left over from a previous `navigate` selection on a `complete` action.
function sanitizeFooterAction(action) {
  if (!action || !action.name) {
    return { name: 'complete', payload: {} };
  }
  if (action.name === 'navigate') {
    const next = action.next || { type: 'screen', name: '' };
    // A navigate action may carry a payload, and it is the only way a value reaches the next
    // screen's `data`, which is in turn the only thing a text component will interpolate. The
    // booking flow's review screen printed "${screen.DATE.form.date}" at the customer for want
    // of this (2026-09-20). Dropped when empty, so nothing else's output moves.
    const payload = objectPayload(action.payload);
    return Object.keys(payload).length > 0
      ? { name: 'navigate', next, payload }
      : { name: 'navigate', next };
  }
  if (action.name === 'data_exchange') {
    return { name: 'data_exchange', payload: objectPayload(action.payload) };
  }
  return { name: 'complete', payload: objectPayload(action.payload) };
}

export const FooterCore = {
  type: 'Footer',
  name: 'Footer',
  icon: '🔲',
  description: 'The primary button at the bottom that submits the form or navigates.',
  category: 'action',
  maxPerScreen: 1,
  getDefaultProperties() {
    return {
      label: 'Submit',
      'on-click-action': {
        name: 'complete',
        payload: {},
      },
    };
  },
  getPropertySchema() {
    return {
      label: {
        type: 'string',
        label: 'Button Label',
        required: true,
      },
      'on-click-action': {
        type: 'action',
        label: 'Action',
        required: true,
      },
    };
  },
  validateProperties(properties, context = {}) {
    const errors = [];
    const warnings = [];

    if (!properties.label || properties.label.trim().length === 0) {
      errors.push('Button label is required');
    }

    // BUG-003: Meta hard-limits CTA button text to 20 characters.
    if (properties.label && properties.label.length > 20) {
      errors.push('Button label must be 20 characters or fewer (Meta limit)');
    }

    const action = properties['on-click-action'];
    if (!action) {
      errors.push('Action is required');
    }

    if (action && !action.name) {
      errors.push('Action type must be specified');
    }

    if (action && action.name === 'navigate') {
      const targetId = action.next?.name;
      if (!targetId) {
        // A navigate action must point at a target screen. WhatsApp rejects this as
        // "Required property 'next' is missing.", catching it locally gives instant feedback and
        // means we never push this broken state to Meta to find out (closes a discrepancy-corpus gap).
        errors.push('Navigate action needs a target screen (select a Next Screen)');
      } else if (
        // A previously-chosen target screen can be deleted, leaving a dangling reference Meta
        // rejects. Only enforce when the screen list is available in context (it isn't for some
        // callers) so the check degrades gracefully rather than firing false positives.
        Array.isArray(context.screens) &&
        !context.screens.some((s) => s.id === targetId)
      ) {
        errors.push('This footer points to a screen that no longer exists, choose a destination.');
      }
    }

    return { errors, warnings };
  },
  transformToWhatsApp(properties) {
    return {
      type: 'Footer',
      label: properties.label,
      'on-click-action': sanitizeFooterAction(properties['on-click-action']),
    };
  },
  transformFromWhatsApp(whatsappComponent) {
    return {
      label: whatsappComponent.label,
      'on-click-action': whatsappComponent['on-click-action'] || {
        name: 'complete',
        payload: {},
      },
    };
  },
};
